import { SubmissionError } from 'redux-form';
import moment from 'moment';
import { flatten } from 'ramda';
import { isEmpty } from 'lodash';

import { getDaysValidationErrors, getSchedulesValidationErrors } from '../../../../../common/helpers/schedules';
import { showNotification } from '../../../../../common/helpers/notifications';
import { TAB_NAMES } from '../hoc/withTabs';
import { checkBarberUsernameAvailability } from '../../../actions';
import { getAllLocationsSchedules, getLocationSchedule } from '../../../formBarbers/barberForm/helpers/scheduleHelpers';
import { LOCATIONS_ALL_ID } from '../constants';
import config from '../../../../../../config';
import {
  getDayOnConflictMessages,
  getScheduleDayConflictMessage,
} from '../../../../../common/schedulesForm/getScheduleConflictTooltip';
import { getBarberShopScheduleWithConflicts, getDaysOnWithConflicts } from '../../../scheduleConflictsHelpers';
import { t } from '../../../../../../modules/infra/i18n';

const isProfileTab = props => props.tabs[props.activeTab].titleKey === TAB_NAMES.PROFILE;
const isLocationsTab = props => props.tabs[props.activeTab].titleKey === TAB_NAMES.LOCATIONS;
const isScheduleTab = props => props.tabs[props.activeTab].titleKey === TAB_NAMES.SCHEDULE;
const isDaysOffOnTab = props => props.tabs[props.activeTab].titleKey === TAB_NAMES.DAYS_OFF_ON;

const asyncValidateUsername = (props, formData) => {
  const usernameEnabled = formData[props.selectedLocation].userEnabled;
  if (!usernameEnabled) {
    return Promise.resolve();
  }

  const usernameValue = formData[props.selectedLocation].username;
  const initialUsernameValue = props.initialValues[props.selectedLocation].username;

  if (usernameValue && usernameValue !== initialUsernameValue) {
    return checkBarberUsernameAvailability(usernameValue)
      .then((response) => {
        if (!response.available) {
          throw new SubmissionError({
            [props.selectedLocation]: { username: t('barber:brand_barber.validation.username_taken') },
            _error: t('barber:brand_barber.validation.failed_to_save'),
          });
        }
      });
  }

  return Promise.resolve();
};

const isDaysOnOffValid = (props, formData) => {
  const hasDayValidationErrors = props.brandBarberShopIds.some((barberShopId) => {
    const barberShopData = formData[barberShopId];
    return getDaysValidationErrors(barberShopData.days_on) || getDaysValidationErrors(barberShopData.days_off);
  });

  if (hasDayValidationErrors) {
    showNotification({
      title: t('barber:brand_barber.validation.error_title'),
      level: 'error',
      message: t('barber:brand_barber.validation.end_time_error'),
    });

    return false;
  }

  return true;
};

const isSchedulesValid = (props, formData) => props.brandBarberShopIds.some((barberShopId) => {
  const schedulesErrors = getSchedulesValidationErrors(formData[barberShopId].schedules);
  return !schedulesErrors.currentScheduleError && !schedulesErrors.futureScheduleError;
});

export const validateBeforeSubmit = async (props, formData) => {
  if (isProfileTab(props)) {
    // throws error if invalid
    await asyncValidateUsername(props, formData);
  }

  if (isLocationsTab(props) && !props.selectedLocations.length) {
    showNotification({
      title: t('barber:brand_barber.validation.error_title'),
      level: 'error',
      message: t('barber:brand_barber.validation.select_location'),
    });

    return false;
  }

  if (isScheduleTab(props) && !isSchedulesValid(props, formData)) {
    return false;
  }

  if (isDaysOffOnTab(props) && !isDaysOnOffValid(props, formData)) {
    return false;
  }

  return true;
};

const getShopDaysOnConflictsWarning = (props, formData, barberShop) => {
  const warningMessageItems = [];

  const shopDaysOnWithConflicts = getDaysOnWithConflicts({
    daysOn: formData[barberShop.id].days_on,
    brandBarber: formData,
    shop: barberShop,
    shopNamesById: props.shopNamesById,
  });

  shopDaysOnWithConflicts.forEach((dayOnWithConflicts) => {
    const isOldDay = moment.utc(dayOnWithConflicts.day, config.dateFormatISO).isBefore(moment.utc(), 'day');
    if (isOldDay) {
      return;
    }

    const hasConflicts = dayOnWithConflicts.conflicts && dayOnWithConflicts.conflicts.length;

    if (hasConflicts) {
      if (!warningMessageItems.length) {
        warningMessageItems.push(`\n${barberShop.name} days on:`);
      }

      warningMessageItems.push(
        `${moment(dayOnWithConflicts.day, config.dateFormatISO).format(config.dateFormat)}: ${
          getDayOnConflictMessages(dayOnWithConflicts.conflicts).join('\n')
        }`
      );
    }
  });

  return warningMessageItems;
};

const getDaysOnConflictsWarning = (props, formData) =>
  props.barberShops.map(barberShop =>
    getShopDaysOnConflictsWarning(props, formData, barberShop)
  );

const getShopScheduleConflictsWarning = ({ shopName, scheduleConflicts, scheduleType }) => {
  const warningMessageItems = [];

  if (!scheduleConflicts || !scheduleConflicts.length) {
    return null;
  }

  scheduleConflicts.forEach((weekdayConflicts) => {
    if (weekdayConflicts.conflicts && weekdayConflicts.conflicts.length) {
      if (!warningMessageItems.length) {
        warningMessageItems.push(`\n${shopName} ${scheduleType} schedule:`);
      }

      const dayName = moment().isoWeekday(weekdayConflicts.weekDay).format(config.fullDayOfWeek);
      warningMessageItems.push(`${dayName}: ${getScheduleDayConflictMessage(weekdayConflicts)}`);
    }
  });

  return warningMessageItems;
};

const getScheduleConflictsWarning = (props, formData) =>
  props.barberShops.map((barberShop) => {
    const shopSchedule = getLocationSchedule(formData, barberShop.id);
    const otherShopsSchedules = getAllLocationsSchedules(
      formData, [barberShop.id, LOCATIONS_ALL_ID]
    );

    return ['current', 'future'].map(scheduleType => (
      getShopScheduleConflictsWarning({
        shopName: barberShop.name,
        scheduleConflicts: getBarberShopScheduleWithConflicts({
          otherShopsSchedules,
          shopSchedule: shopSchedule[scheduleType],
          shopNamesById: props.shopNamesById,
          shop: barberShop,
        }),
        scheduleType,
      })
    ));
  });

export const getValidationWarnings = (props, formData) =>
  flatten([
    getScheduleConflictsWarning(props, formData),
    getDaysOnConflictsWarning(props, formData),
  ])
    .filter(warning => !!warning && !isEmpty(warning))
    .join('\n');
